/** @format */

import { buildPaperId, getPaperIdFromParentQuestionId, Paper, type QuestionSeries } from "@question-bank/shared";

/**
 * The minimal shape needed to resolve which paper a question belongs to.
 *
 * A question carries its owning paper id directly (`paperId`, QB2-223) when
 * created through the API. Seeded/legacy questions instead carry a
 * `parentQuestionId` whose leading segments ARE the paper id. Both are
 * supported; the paper-level identity parts (`brand` + `paperCode` + `series`)
 * are the last-resort fallback.
 */
export interface PaperIdentity {
	brand: string;
	paperCode: string;
	series: QuestionSeries;
}

export type PaperIdentifiable = { paperId?: string; parentQuestionId?: string } & Partial<PaperIdentity>;

/**
 * Resolve the deterministic paper id for a question-like object.
 *
 * Order of preference:
 *  1. `paperId` — set directly on API-created questions.
 *  2. `parentQuestionId` — seeded questions embed the paper id as its leading
 *     segments (`{paperId}--{root}`); derive it. This avoids depending on the
 *     question's own `series.season`, which on seeded rows may still be a plain
 *     string rather than a Core reference (so `season.code` would be undefined).
 *  3. Rebuild from `brand`/`paperCode`/`series.season.code` as a last resort.
 */
export const paperIdOf = (item: PaperIdentifiable): string => {
	if (item.paperId) {
		return item.paperId;
	}
	if (item.parentQuestionId) {
		const derived = getPaperIdFromParentQuestionId(item.parentQuestionId);
		if (derived) {
			return derived;
		}
	}
	return buildPaperId({
		brand: item.brand as string,
		paperCode: item.paperCode as string,
		// `buildPaperId` keys on the Core season CODE (QB2-223).
		series: {
			season: (item.series as QuestionSeries).season.code,
			year: (item.series as QuestionSeries).year,
		},
	});
};

/**
 * Whether the question's paper carries any attachments (i.e. it has additional
 * resources), given the set of paper ids known to have attachments.
 */
export const hasAdditionalResources = (item: PaperIdentifiable, paperIdsWithAttachments: Set<string>): boolean =>
	paperIdsWithAttachments.has(paperIdOf(item));

export type AttachmentLanguage = "en" | "cy";

export interface AttachmentDownload {
	/** Stored S3 asset key for the PDF. */
	assetKey: string;
	/** Suggested download filename. */
	filename: string;
}

const sanitise = (value: string): string =>
	value
		.toLocaleLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

const buildAttachmentFilename = (
	paper: Paper,
	attachment: Paper["attachments"][number],
	language: AttachmentLanguage,
): string => {
	const parts = [paper.paperCode, attachment.label ?? "attachment", language].map(sanitise).filter(Boolean);
	return `${parts.join("_") || "attachment"}.pdf`;
};

/**
 * Collect the deduplicated set of attachment PDFs to bundle alongside an
 * exported paper.
 *
 * - Papers referenced by multiple questions contribute their attachments once
 *   (dedupe by paper, then globally by asset key).
 * - For each requested language, the attachment's PDF for that language is
 *   included when present.
 *
 * @param papersById       Papers keyed by their deterministic id.
 * @param questionPaperIds Paper ids referenced by the exported questions (may repeat).
 * @param languages        Languages to export (e.g. `["en"]`, or `["en", "cy"]` when a Welsh paper is also selected).
 */
export const collectAttachmentDownloads = (
	papersById: Map<string, Paper>,
	questionPaperIds: string[],
	languages: AttachmentLanguage[],
): AttachmentDownload[] => {
	const seenAssets = new Set<string>();
	const downloads: AttachmentDownload[] = [];

	for (const paperId of new Set(questionPaperIds)) {
		const paper = papersById.get(paperId);
		if (!paper) {
			continue;
		}
		for (const attachment of paper.attachments ?? []) {
			for (const language of languages) {
				const otherLanguage: AttachmentLanguage = language === "en" ? "cy" : "en";
				const actualLanguage = attachment[language] ? language : otherLanguage;
				const assetKey = attachment[actualLanguage];
				if (!assetKey || seenAssets.has(assetKey)) {
					continue;
				}
				seenAssets.add(assetKey);
				downloads.push({ assetKey, filename: buildAttachmentFilename(paper, attachment, actualLanguage) });
			}
		}
	}

	return downloads;
};
